import { addDays } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AccessorySelection from './components/AccessorySelection';
import BikeCatalog from './components/BikeCatalog';
import BookingStepper from './components/BookingStepper';
import CustomerInfo from './components/CustomerInfo';
import DateSelection from './components/DateSelection';
import DeliveryOptions from './components/DeliveryOptions';
import Footer from './components/Footer';
import Header from './components/Header';
import LanguageSelector from './components/LanguageSelector';
import MaintenanceScreen from './components/MaintenanceScreen';
import PaymentInfo from './components/PaymentInfo';
import SupportErrorBanner from './components/SupportErrorBanner';
import { API_ENDPOINTS, buildApiUrl } from './config/api';
import { BookingProvider, useBooking } from './contexts/BookingContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { SupportApiErrorProvider } from './contexts/SupportApiErrorContext';
import { useIframeAutoResize } from './hooks/useIframeAutoResize';
import { useTranslation } from './hooks/useTranslation';
import { calculateCompletePricing, calculateDurationInDays, calculateVehiclesTotal, recalculateAccessoriesAndDeliveryPricing } from './services/pricingService';
import { sendScrollToTopMessage } from './utils/embedMessaging';
import { notifySupportApiNetworkError, notifySupportApiServerError } from './utils/supportApiErrorNotifier';

const steps = [
  'stepper.step1',
  'stepper.step2',
  'stepper.step3',
  'stepper.step4',
  'stepper.step5',
  'stepper.step6'
];

const isMaintenanceMode = import.meta.env.VITE_MAINTENANCE_MODE === 'true';

function AppContent() {
  const [activeStep, setActiveStep] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkApplied = useRef(false);
  // Filtros aplicados por deep link (guardados antes de limpiar la URL para pasarlos a BikeCatalog)
  const [deepLinkFilters, setDeepLinkFilters] = useState({ category: null, model: null });
  const { bookingData, updateBookingData, updatePricingWithSeasons, resetToStep, isLoaded } = useBooking();

  const { t } = useTranslation();

  useIframeAutoResize();

  // Deep link: solo se permite llegar al paso 2 con ?step=2 (y opcionalmente category/model). Tras aplicar, se limpian los query params.
  useEffect(() => {
    if (!isLoaded || deepLinkApplied.current) return;
    const stepParam = searchParams.get('step') || searchParams.get('start');
    // Solo permitir paso 2; ignorar step=3, 4, 5, 6, etc.
    if (stepParam !== '2') return;

    deepLinkApplied.current = true;
    const category = searchParams.get('category');
    const model = searchParams.get('model');
    setDeepLinkFilters({ category: category || null, model: model || null });

    const today = new Date();
    const tomorrow = addDays(today, 1);
    const startDateTime = new Date(today);
    startDateTime.setHours(9, 0, 0, 0);
    const endDateTime = new Date(tomorrow);
    endDateTime.setHours(21, 0, 0, 0);
    const defaultDates = {
      start: startDateTime.toISOString(),
      end: endDateTime.toISOString(),
      startTime: '09:00:00',
      endTime: '21:00:00'
    };
    updateBookingData({ dates: defaultDates });
    setActiveStep(1);
    setSearchParams({}, { replace: true });
  }, [isLoaded, searchParams, updateBookingData, setSearchParams]);

  // Scroll al inicio cuando cambia el paso para que el usuario vea siempre el contenido desde arriba.
  // Si estamos embebidos en un iframe, además pedimos a la página padre que haga scroll hasta el
  // propio iframe, porque window.scrollTo aquí solo mueve el viewport interno del iframe.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    sendScrollToTopMessage();
  }, [activeStep]);

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = async () => {
    const newStep = activeStep - 1;
    setActiveStep(newStep);
    await resetToStep(newStep);
  };

  const handleDateSelect = (dates) => {
    updateBookingData({ dates });
    handleNext();
  };

  const handleBikeSelect = async (vehicles) => {
    try {
      await updatePricingWithSeasons({
        selectedVehicles: vehicles
      });
      handleNext();
    } catch (error) {
      console.error('Error calculando precios:', error);
      // Fallback a cálculo local
      const pricing = calculateCompletePricing(
        vehicles, 
        {}, 
        bookingData.dates, 
        [], 
        bookingData.deliveryOption?.type || 'pickup'
      );
      updateBookingData({
        selectedVehicles: vehicles,
        pricing
      });
      handleNext();
    }
  };

  const handleAccessorySelect = async (accessoriesData) => {
    try {
      // Convertir accessoriesByModel a una lista plana de accesorios para el cálculo de precios
      const allAccessories = Object.values(accessoriesData.accessoriesByModel || {}).flat();
      
      await updatePricingWithSeasons({
        selectedAccessories: accessoriesData.selectedAccessories,
        accessories: allAccessories
      });
      handleNext();
    } catch (error) {
      console.error('Error calculando precios:', error);
      // Fallback a cálculo local
      const allAccessories = Object.values(accessoriesData.accessoriesByModel || {}).flat();
      // Pasar el objeto deliveryOption completo para mantener el coste calculado
      const deliveryOptionForPricing = bookingData.deliveryOption || 'pickup';
      
      const pricing = calculateCompletePricing(
        bookingData.selectedVehicles, 
        accessoriesData.selectedAccessories,
        bookingData.dates, 
        allAccessories,
        deliveryOptionForPricing
      );
      updateBookingData({
        selectedAccessories: accessoriesData.selectedAccessories,
        accessories: allAccessories,
        pricing
      });
      handleNext();
    }
  };

  const handleDeliverySelect = async (option) => {
    try {
      await updatePricingWithSeasons({
        deliveryOption: option
      });
      handleNext();
    } catch (error) {
      console.error('Error calculando precios:', error);
      // Fallback a cálculo local
      const pricing = calculateCompletePricing(
        bookingData.selectedVehicles, 
        bookingData.selectedAccessories,
        bookingData.dates, 
        bookingData.accessories,
        option // Pasar el objeto completo con type y cost
      );
      
      updateBookingData({
        deliveryOption: option,
        pricing
      });
      handleNext();
    }
  };

  const handleCustomerInfo = (info) => {
    updateBookingData({
      customerInfo: info.customerInfo,
      inseamCmByVehicleId: info.inseamCmByVehicleId || {},
      temporaryBlockIds: info.temporaryBlockIds,
      bookingId: info.bookingId,
    });
    handleNext();
  };

  const handlePaymentSubmit = async (paymentInfo) => {
    try {
      // Confirmar la reserva
      const response = await fetch(buildApiUrl(`${API_ENDPOINTS.BOOKING_CONFIRM}/${bookingData.bookingId}`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          temporaryBlockIds: bookingData.temporaryBlockIds
        })
      });

      if (!response.ok) {
        notifySupportApiServerError(response.status);
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al confirmar la reserva');
      }

      updateBookingData({
        paymentInfo
      });
      handleNext();
    } catch (error) {
      notifySupportApiNetworkError(error);
      console.error('Error al confirmar la reserva:', error);
      // Aquí podrías manejar el error, por ejemplo mostrando un mensaje al usuario
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return <DateSelection 
          onDateSelect={handleDateSelect}
          initialDates={bookingData.dates}
        />;
      case 1:
        return <BikeCatalog 
          selectedDates={bookingData.dates}
          onNext={handleBikeSelect}
          onBack={handleBack}
          deliveryType={bookingData.deliveryOption?.type || 'pickup'}
          initialCategory={deepLinkFilters.category}
          initialModel={deepLinkFilters.model}
        />;
      case 2:
        return <AccessorySelection 
          selectedVehicles={bookingData.selectedVehicles}
          selectedDates={bookingData.dates}
          selectedAccessories={bookingData.selectedAccessories}
          bikesTotal={bookingData.pricing?.vehicles?.total ?? calculateVehiclesTotal(bookingData.selectedVehicles, calculateDurationInDays(bookingData.dates?.start, bookingData.dates?.end))}
          onNext={handleAccessorySelect}
          onBack={handleBack}
          onPricingUpdate={(newAccessories, accessoriesByModel) => {
            // Convertir accessoriesByModel a una lista plana de accesorios para el cálculo de precios
            const allAccessories = Object.values(accessoriesByModel || {}).flat();

            // Pasar el objeto deliveryOption completo (con type, cost, address, distance) para mantener el coste calculado
            const deliveryOptionForPricing = bookingData.deliveryOption || 'pickup';

            // Importante: NO recalcular el precio de las bicis aquí (calculateCompletePricing usa una
            // fórmula de duración local distinta a la del backend y puede desincronizar el total mostrado
            // del que realmente se cobrará). Se mantiene el total de bicis ya cotizado (con temporadas).
            const pricing = recalculateAccessoriesAndDeliveryPricing(
              bookingData.pricing?.vehicles,
              newAccessories,
              bookingData.dates,
              allAccessories,
              deliveryOptionForPricing
            );
            updateBookingData({ pricing });
          }}
        />;
      case 3:
        return <DeliveryOptions 
          onSelectOption={handleDeliverySelect}
          initialDeliveryOption={bookingData.deliveryOption}
        />;
      case 4:
        return <CustomerInfo 
          onInfoSubmit={handleCustomerInfo}
          bookingData={{
            ...bookingData,
            accessories: bookingData.accessories || []
          }}
        />;
      case 5:
        return <PaymentInfo 
          onPaymentSubmit={handlePaymentSubmit}
          bookingData={bookingData}
        />;
      default:
        return null;
    }
  };

  const translatedSteps = steps.map(step => t(step));

  if (isMaintenanceMode) {
    return (
      <div className="app-with-layout">
        {/* Header y Footer deshabilitados: el widget se embebe vía iframe en una página
            WordPress que ya aporta su propio header/footer del sitio. */}
        {/* <Header /> */}
        <div className="app-main-container-maintenance max-w-[95%] xl:max-w-[1600px] mx-auto p-0 min-h-screen flex flex-col">
          <MaintenanceScreen />
        </div>
        {/* <Footer /> */}
      </div>
    );
  }

  // Mostrar loading mientras se cargan los datos del localStorage
  if (!isLoaded) {
    return (
      <div className="app-main-container max-w-[95%] xl:max-w-[1600px] mx-auto p-4 md:p-6 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-EBC122 mx-auto mb-4"></div>
          <p className="text-2c3e50">Cargando datos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app-with-layout">
      {/* Header y Footer deshabilitados: el widget se embebe vía iframe en una página
          WordPress que ya aporta su propio header/footer del sitio. */}
      {/* <Header /> */}
      <div className="app-main-container max-w-[95%] xl:max-w-[1600px] mx-auto p-0 md:p-6 min-h-screen">
        <div className="mb-10 pb-5 border-b-2 border-EBC122 pl-6 md:pl-0">
          <div className="flex justify-between items-start mb-4">
            <div className="text-left">
              <h1 className="m-0 text-2c3e50 text-3xl font-bold font-orbitron mb-2.5">
                {t('booking.title')}
              </h1>
              <p className="m-0 text-6c757d text-base font-inter">
                {t('booking.subtitle')}
              </p>
            </div>
            {/* <LanguageSelector /> */}
          </div>
        </div>

        <BookingStepper 
          activeStep={activeStep} 
          steps={translatedSteps}
          onBack={handleBack}
          hideBackButton={activeStep === 5 && bookingData.isCompleted}
        />
        <SupportErrorBanner />
        {renderStepContent(activeStep)}
      </div>
      {/* <Footer /> */}
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <SupportApiErrorProvider>
        <BookingProvider>
          <AppContent />
        </BookingProvider>
      </SupportApiErrorProvider>
    </LanguageProvider>
  );
}

export default App;
